import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EditNoteIcon from "@mui/icons-material/EditNote";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Close } from "@mui/icons-material";
import { useState } from "react";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { useDeletePartnerMutation } from "../../Slices/roomNateApis";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useGetAllZoneQuery } from "../../Slices/roomNateConstantApis";
import AddPartnerModal from "./AddPartnerModel";
import AddPartnerZonesModal from "./AddPartnerZonesModal";

/** try / staging partners listed first (no special badge — order only). */
const PRIORITY_DOMAIN_ORDER = ["try.magicfurnish.com", "staging.magicfurnish.com"];

function normalizePartnerDomain(domain) {
  if (!domain) return "";
  let s = String(domain).toLowerCase().trim();
  s = s.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  return s.split(":")[0];
}

function sortPartnersForDisplay(partners) {
  if (!partners?.length) return [];
  const priority = PRIORITY_DOMAIN_ORDER.map((host) =>
    partners.find((p) => normalizePartnerDomain(p?.domain) === host)
  ).filter(Boolean);
  const priorityIds = new Set(priority.map((p) => p._id));
  const rest = partners.filter((p) => !priorityIds.has(p._id));
  return [...priority, ...rest];
}

function PartnerCard({
  row,
  packages,
  setSelectedPartner,
  setEditPartner,
  setEditPartnerZones,
  onDeleteClick,
  featured,
}) {
  const packageName = packages?.find((p) => p._id === row?.partnerPackage)?.name;

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: featured ? "primary.main" : "divider",
        borderWidth: featured ? 2 : 1,
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: featured ? (theme) => alpha(theme.palette.primary.main, 0.04) : "background.paper",
        transition: "box-shadow 0.15s, border-color 0.15s",
        "&:hover": {
          boxShadow: featured
            ? (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}`
            : "0 2px 12px rgba(0,0,0,0.08)",
          borderColor: featured ? "primary.dark" : "text.disabled",
        },
      }}
    >
      <CardContent
        sx={{ flexGrow: 1, pb: 1, cursor: "pointer" }}
        onClick={() => setSelectedPartner(row)}
      >
        {/* 1. Name */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 1.5, fontSize: "1rem", letterSpacing: 0.2 }}
        >
          {row?.title || "Untitled"}
        </Typography>

        {/* 2. Subdomain + floor plan count */}
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
          <Chip
            label={row?.domain || "No domain"}
            size="small"
            sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
          />
          <Chip
            label={`${row?.count ?? 0} floor plan${row?.count !== 1 ? "s" : ""}`}
            size="small"
            variant="outlined"
          />
        </Stack>

        {/* 3. Hex + package */}
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
          {row?.primaryColor && (
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: row.primaryColor,
                  border: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                {row.primaryColor.toUpperCase()}
              </Typography>
            </Stack>
          )}
          {packageName && <Chip label={packageName} size="small" variant="outlined" />}
        </Stack>

        {/* 4. Auto-approve / guest upload */}
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip
            label="Auto-approve users"
            size="small"
            color={row?.autoApproveUser ? "success" : "error"}
            variant={row?.autoApproveUser ? "filled" : "outlined"}
          />
          <Chip
            label="Guest upload"
            size="small"
            color={row?.allowGuestUpload ? "success" : "error"}
            variant={row?.allowGuestUpload ? "filled" : "outlined"}
          />
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ px: 1.5, py: 1, gap: 0.5 }}>
        <Tooltip title="View">
          <IconButton size="small" onClick={() => setSelectedPartner(row)}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => setEditPartner(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Update Zones">
          <IconButton size="small" onClick={() => setEditPartnerZones(row)}>
            <EditNoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!row?.isSelf && (
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDeleteClick(row._id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
}

export default function PartnersTable({
  partnersData,
  setSelectedPartner,
  packages,
}) {
  const { currentUserSessionStorage } = useGlobalAuthContext();

  const [selectedId, setSelectedId] = React.useState();
  const [open, setOpen] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const [editPartner, setEditPartner] = useState({});
  const [editPartnerZones, setEditPartnerZones] = useState({});

  const [deletePartner, resultStats] = useDeletePartnerMutation();

  const { data: zonesData, isFetching: zonesFetching } = useGetAllZoneQuery("", {
    skip: !currentUserSessionStorage,
  });

  React.useEffect(() => {
    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => setSuccessAlert(false), 2000);
    }
    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => setErrorAlert(false), 2000);
    }
  }, [resultStats]);

  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDelete = () => {
    handleClose();
    deletePartner(selectedId);
  };

  const sortedPartners = React.useMemo(
    () => sortPartnersForDisplay(partnersData),
    [partnersData]
  );

  return (
    <>
      {successAlert && <SuccessAlert title="Partner has been deleted successfully!" />}
      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Partners{" "}
          {partnersData?.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary">
              ({partnersData.length})
            </Typography>
          )}
        </Typography>
        <Stack direction="row" gap={1}>
          <AddPartnerModal
            editPartner={editPartner}
            setEditPartner={setEditPartner}
            packages={packages}
          />
          <AddPartnerZonesModal
            editPartner={editPartnerZones}
            zonesData={
              zonesFetching
                ? []
                : [
                    ...(zonesData || []),
                    {
                      value: "lounging,dining",
                      label: "Living + Dining",
                      img: "/images/zones/Working.svg",
                      capacity: false,
                    },
                    {
                      value: "custom",
                      label: "Manual Item",
                      img: "/images/zones/Working.svg",
                      capacity: false,
                    },
                  ]
            }
            setEditPartner={setEditPartnerZones}
          />
        </Stack>
      </Box>

      {/* Card grid */}
      {!partnersData || partnersData.length === 0 ? (
        <Typography color="text.secondary">No partners found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {sortedPartners.map((row, index) => {
            const featured = PRIORITY_DOMAIN_ORDER.includes(
              normalizePartnerDomain(row?.domain)
            );
            return (
              <Grid item xs={12} sm={6} lg={4} key={row._id || index}>
                <PartnerCard
                  row={row}
                  packages={packages}
                  setSelectedPartner={setSelectedPartner}
                  setEditPartner={setEditPartner}
                  setEditPartnerZones={setEditPartnerZones}
                  onDeleteClick={handleClickOpen}
                  featured={featured}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm the Action?</DialogTitle>
        <Box position="absolute" top={10} right={0}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Do you really want to delete this partner?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
